// src/components/skills/skill-constants.ts
//
// Constantes + helpers compartilhados da Reforma UX §6.7 (Skills).
// Badges, labels PT-BR e normalizadores de jsonb (references/evals) que antes
// viviam inline na SkillDetailPage.

import type { SkillScope } from '@/lib/api';
import { fmtNumber } from '@/lib/format';

export const SCOPE_BADGE: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
  global: 'info',
  tenant: 'success',
  agent: 'warning',
};

export const MATURITY_BADGE: Record<string, 'default' | 'info' | 'success' | 'warning' | 'danger'> = {
  draft: 'default',
  experimental: 'warning',
  beta: 'info',
  stable: 'success',
  deprecated: 'danger',
};

/** skills.status real do backend: draft | published | deprecated. */
export const STATUS_BADGE: Record<string, 'default' | 'info' | 'success' | 'warning' | 'danger'> = {
  draft: 'default',
  published: 'success',
  deprecated: 'danger',
};

/** Labels PT-BR do wireframe ("ativa"/"arquivada") mapeadas pros valores reais. */
export const STATUS_LABELS: Record<string, string> = {
  published: 'ativa',
  draft: 'draft',
  deprecated: 'arquivada',
};

export const SCOPE_FILTER_OPTIONS: Array<{ value: SkillScope | ''; label: string }> = [
  { value: '', label: 'Scope: todos' },
  { value: 'tenant', label: 'tenant' },
  { value: 'global', label: 'global' },
  { value: 'agent', label: 'agent' },
];

export const STATUS_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Status: todas' },
  { value: 'published', label: 'ativa' },
  { value: 'draft', label: 'draft' },
  { value: 'deprecated', label: 'arquivada' },
];

/** Labels PT-BR das 6 dimensões reais do scoring_6d (engine/quality/scoring-6d.ts). */
export const SCORING_6D_LABELS: Record<string, string> = {
  relevancia: 'Relevância',
  negocios: 'Negócios',
  conteudo: 'Conteúdo',
  operacao: 'Operação',
  novidade: 'Novidade',
  credibilidade: 'Credibilidade',
};

/** Chaves do jsonb scoring_6d que NÃO são dimensões (meta). */
export const SCORING_6D_META_KEYS = new Set(['weighted_mean', 'updated_at']);

/** C2 — % sucesso da lista: success_rate 0-1 → "97%"; null → "—" (não inventa). */
export function formatSkillSuccessRate(rate: number | null | undefined): string {
  if (rate == null || Number.isNaN(rate)) return '—';
  return `${fmtNumber(Math.round(rate * 100))}%`;
}

export function formatDurationMs(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${fmtNumber(ms / 1000, 1)}s`;
  // SKL-C (admin 2026-06-12): nada de "15,5min" — tempo legível por unidade
  // (h/min/s): "15min 30s", "1h 5min".
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const min = Math.floor((totalSec % 3600) / 60);
  const sec = totalSec % 60;
  if (h > 0) return min > 0 ? `${h}h ${min}min` : `${h}h`;
  return sec > 0 ? `${min}min ${sec}s` : `${min}min`;
}

export interface NormalizedReference {
  name: string;
  content: string;
}

/**
 * references_bundle é jsonb genérico: array canônico [{name,content}] OU dict
 * {'arquivo.md': '...'}. Normaliza pra array (evita TypeError em .map).
 */
export function normalizeReferences(raw: unknown): NormalizedReference[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return (raw as Array<{ name?: string; content?: unknown }>).map((r, i) => ({
      name: r.name ?? `reference-${i + 1}`,
      content: typeof r.content === 'string' ? r.content : JSON.stringify(r.content ?? '', null, 2),
    }));
  }
  if (typeof raw === 'object') {
    return Object.entries(raw as Record<string, unknown>).map(([name, content]) => ({
      name,
      content: typeof content === 'string' ? content : JSON.stringify(content, null, 2),
    }));
  }
  return [];
}

/**
 * evals_contract é jsonb genérico: array de cases OU {cases:[...]} OU dict.
 * Normaliza pra array de objetos.
 */
export function normalizeEvals(raw: unknown): Array<Record<string, unknown>> {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as Array<Record<string, unknown>>;
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj['cases'])) return obj['cases'] as Array<Record<string, unknown>>;
    return Object.values(obj).filter(
      (v): v is Record<string, unknown> => typeof v === 'object' && v !== null,
    );
  }
  return [];
}

/**
 * Conta steps do SKILL.md (lista numerada sob heading "## Steps" / "## Etapas").
 * Best-effort — backend não expõe steps como campo (GAP reportado §6.7).
 */
export function parseStepsCount(skillMd: string | null | undefined): number | null {
  if (!skillMd) return null;
  // SKL-C: formato canônico real do bucket = headings `### STEP N — nome`
  // (sob ## §3 Workflow). A heurística antiga (## Steps + lista numerada)
  // não casava nada e a contagem vinha sempre null.
  const stepHeadings = skillMd.match(/^#{2,4}\s*STEP\s+\d+\b/gim);
  if (stepHeadings && stepHeadings.length > 0) return stepHeadings.length;
  const lines = skillMd.split('\n');
  const headingIdx = lines.findIndex((l) => /^#{2,3}\s*(steps|etapas)\b/i.test(l.trim()));
  if (headingIdx === -1) return null;
  let count = 0;
  for (let i = headingIdx + 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (/^#{1,6}\s/.test(line)) break; // próximo heading
    if (/^\d+[.)]\s/.test(line) || /^-\s+\*\*?step/i.test(line)) count++;
  }
  return count > 0 ? count : null;
}
